/**
 * ClubEventsManagePage - Event Management for Club Leaders/Owners (#872)
 *
 * Management view for club events with edit, cancel, and duplicate actions.
 * Only accessible to club leaders and owners.
 *
 * #1207: Direct delete with scope selection for recurring events
 * #1208: Unified rows - series collapsed with stacked card, standalone events flat
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useClub } from '../hooks/useClubs';
import { useManageEvents, useDeleteEvent, useDeleteSeries } from '../hooks/useEvents';
import { useAuth } from '../hooks/useAuth';
import { useRegisterFab } from '../contexts/PWAContext';
import { TopBar, Button, EmptyState, Avatar, ConfirmDialog, DeleteScopeModal, EventSeriesRow, SeriesDeleteModal, Skeleton } from '../components';
import { Plus, Calendar, Users, MoreVertical, Edit, Eye, Copy, XCircle, Lock } from 'lucide-react';
import { EventDateTime } from '../components/ui/DateTimeDisplay';
import { POST_EVENT_VISIBILITY_BUFFER_SECONDS } from '../lib/datetime';
import { useNowSeconds } from '../hooks/useNowSeconds';
import type { DeleteScope, ManageRow, EventSeries } from '../lib/api/events';

type FilterTab = 'upcoming' | 'past' | 'all';

interface EventRowProps {
  event: {
    id: string;
    short_code?: string;
    title: string;
    starts_at: number;
    ends_at?: number | null;
    rsvp_count?: number;
    location_name?: string | null;
    training_group_id?: string | null;
    is_recurring?: number;
  };
  onEdit: () => void;
  onViewRSVPs: () => void;
  onDuplicate: () => void;
  onCancel: () => void;
}

function EventManagementRow({ event, onEdit, onViewRSVPs, onDuplicate, onCancel }: EventRowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  // #1693: live nowSeconds via useNowSeconds keeps "isPast" current on
  // long-lived list views (60s ticks). #1782 buffer (1h after end) applied
  // inline so events still happening / recently ended stay in upcoming.
  // Inline derivation (not isPastEvent()) keeps the impure Date.now()
  // read out of render — see purity rule from #1689.
  const nowSeconds = useNowSeconds();
  const effectiveEnd = event.ends_at ?? event.starts_at;
  const isPast = effectiveEnd + POST_EVENT_VISIBILITY_BUFFER_SECONDS < nowSeconds;

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-text-primary truncate">
            {event.title}
          </h3>
          <div className="flex items-center gap-2 mt-1 text-sm text-text-secondary">
            <Calendar className="w-3.5 h-3.5 shrink-0" />
            <EventDateTime timestamp={event.starts_at} className="text-sm" />
          </div>
          {event.location_name && (
            <p className="text-sm text-text-muted mt-1 truncate">
              {event.location_name}
            </p>
          )}
          <div className="flex items-center gap-4 mt-2">
            <span className="text-sm text-accent">
              <Users className="w-3.5 h-3.5 inline mr-1" />
              {event.rsvp_count || 0} RSVPs
            </span>
            {isPast && (
              <span className="text-xs text-text-muted bg-oro-surface-alt px-2 py-0.5 rounded">
                Past
              </span>
            )}
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center justify-center w-11 h-11 rounded-full hover:bg-oro-surface-alt transition-colors"
            aria-label="Event actions"
          >
            <MoreVertical className="w-5 h-5 text-text-muted" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-12 z-50 w-48 bg-oro-surface border border-oro-border rounded-xl shadow-float py-1 overflow-hidden">
              <button
                onClick={() => { onEdit(); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-oro-surface-alt transition-colors"
              >
                <Edit className="w-4 h-4 text-text-muted" />
                Edit Event
              </button>
              <button
                onClick={() => { onViewRSVPs(); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-oro-surface-alt transition-colors"
              >
                <Eye className="w-4 h-4 text-text-muted" />
                View RSVPs
              </button>
              <button
                onClick={() => { onDuplicate(); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-oro-surface-alt transition-colors"
              >
                <Copy className="w-4 h-4 text-text-muted" />
                Duplicate Event
              </button>
              <button
                onClick={() => { onCancel(); setMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-oro-error hover:bg-oro-surface-alt transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Cancel Event
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClubEventsManagePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('upcoming');

  // Delete modal state - for standalone events (#1207)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    title: string;
    isRecurring: boolean;
  } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteScope, setShowDeleteScope] = useState(false);

  // Series delete modal state (#1208)
  const [seriesDeleteTarget, setSeriesDeleteTarget] = useState<EventSeries | null>(null);
  const [showSeriesDelete, setShowSeriesDelete] = useState(false);

  // Fetch club details
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');

  // Fetch unified manage rows (#1208)
  const { data: rows = [], isLoading: rowsLoading, error: rowsError } = useManageEvents(
    clubData?.club.id || '',
    activeFilter
  );

  // Delete mutations
  const deleteEvent = useDeleteEvent(clubData?.club.id || '');
  const deleteSeries = useDeleteSeries(clubData?.club.id || '');

  const loading = clubLoading || rowsLoading;
  const error = clubError || rowsError;
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeader = membership?.role === 'leader' || membership?.is_owner;
  // Suppress the PWA install prompt while the create-event FAB is on screen (#3096).
  // The FAB renders only once the leader-gated main content is shown, so mirror
  // that condition rather than registering during loading/error/redirect states.
  useRegisterFab(!loading && !error && !!club && !!slug && !!isLeader);

  // Redirect non-leaders
  useEffect(() => {
    if (!loading && !isLeader && slug) {
      navigate(`/clubs/${slug}`);
    }
  }, [loading, isLeader, slug, navigate]);

  // Event actions
  const handleEdit = (eventId: string, shortCode?: string) => {
    navigate(`/event/${shortCode || eventId}/edit`);
  };

  const handleViewRSVPs = (shortCode?: string, eventId?: string) => {
    navigate(`/event/${shortCode || eventId}`);
  };

  const handleDuplicate = (eventId: string) => {
    navigate(`/clubs/${slug}/events/create?duplicate=${eventId}`);
  };

  // Cancel standalone event (#1207)
  const handleCancelEvent = (eventId: string, title: string, isRecurringEvent: boolean) => {
    setDeleteTarget({ id: eventId, title, isRecurring: isRecurringEvent });

    if (isRecurringEvent) {
      setShowDeleteScope(true);
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    submitDelete();
  };

  const handleDeleteScopeSelect = (scope: DeleteScope) => {
    setShowDeleteScope(false);
    submitDelete(scope);
  };

  const submitDelete = (deleteScope?: DeleteScope) => {
    if (!deleteTarget) return;

    deleteEvent.mutate(
      { eventId: deleteTarget.id, deleteScope },
      {
        onSuccess: (response) => {
          const count = response.deleted_count || 1;
          if (deleteScope === 'this_and_future' && count > 1) {
            toast.success(`Deleted ${count} events`);
          } else {
            toast.success('Event deleted');
          }
          setDeleteTarget(null);
        },
        onError: () => {
          setDeleteTarget(null);
        },
      }
    );
  };

  // Series actions (#1208)
  const handleSeriesTap = (series: EventSeries) => {
    navigate(`/clubs/${slug}/series/${series.series_id}`);
  };

  const handleSeriesEdit = (series: EventSeries) => {
    navigate(`/clubs/${slug}/series/${series.series_id}/edit`);
  };

  const handleSeriesCancel = (series: EventSeries) => {
    setSeriesDeleteTarget(series);
    setShowSeriesDelete(true);
  };

  const handleSeriesDeleteConfirm = (scope: 'next' | 'all_future') => {
    if (!seriesDeleteTarget) return;

    deleteSeries.mutate(
      { seriesId: seriesDeleteTarget.series_id, scope },
      {
        onSuccess: (response) => {
          toast.success(`Deleted ${response.deleted_count} event${response.deleted_count > 1 ? 's' : ''}`);
          setSeriesDeleteTarget(null);
          setShowSeriesDelete(false);
        },
        onError: () => {
          setSeriesDeleteTarget(null);
          setShowSeriesDelete(false);
        },
      }
    );
  };

  // Count total events/series for header
  const totalCount = rows.reduce((acc, row) => {
    if (row.type === 'series') {
      return acc + row.series.upcoming_count;
    }
    return acc + 1;
  }, 0);

  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Manage Events" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-oro-surface border border-oro-border rounded-xl p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !club || !slug || !isLeader) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Manage Events" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<Lock className="w-12 h-12" />}
              title="Access Denied"
              message="You need to be a club leader or owner to manage events."
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title={club.name}
        subtitle="Manage Events"
        onBack={() => navigate(-1)}
        showClubSwitcher
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Header with Create Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar
                name={club.name}
                profile_image_url={club.hero_image_url}
                size="sm"
              />
              <div>
                <h2 className="font-semibold text-text-primary">{club.name}</h2>
                <p className="text-sm text-text-muted">{totalCount} events</p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/clubs/${slug}/events/create`)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Create
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1 bg-oro-surface-alt border border-oro-border rounded-xl p-1">
            {[
              { key: 'upcoming' as const, label: 'Upcoming' },
              { key: 'past' as const, label: 'Past' },
              { key: 'all' as const, label: 'All' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === tab.key
                    ? 'bg-oro-surface text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Unified Rows - Series + Standalone Events (#1208) */}
          {rows.length > 0 ? (
            <div className="space-y-3 pb-20">
              {rows.map((row: ManageRow) => {
                if (row.type === 'series') {
                  return (
                    <EventSeriesRow
                      key={row.series.series_id}
                      series={row.series}
                      onTap={() => handleSeriesTap(row.series)}
                      onEdit={() => handleSeriesEdit(row.series)}
                      onCancel={() => handleSeriesCancel(row.series)}
                    />
                  );
                }

                const event = row.event;
                return (
                  <EventManagementRow
                    key={event.id}
                    event={{
                      id: event.id,
                      short_code: event.short_code,
                      title: event.title,
                      starts_at: event.starts_at,
                      ends_at: event.ends_at ?? undefined,
                      rsvp_count: event.rsvp_count,
                      location_name: event.location_name ?? undefined,
                      training_group_id: event.training_group_id,
                      is_recurring: event.is_recurring,
                    }}
                    onEdit={() => handleEdit(event.id, event.short_code)}
                    onViewRSVPs={() => handleViewRSVPs(event.short_code, event.id)}
                    onDuplicate={() => handleDuplicate(event.id)}
                    onCancel={() => handleCancelEvent(event.id, event.title, !!event.is_recurring || !!event.training_group_id)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="py-12">
              <EmptyState
                icon={<Calendar className="w-12 h-12" />}
                title={
                  activeFilter === 'upcoming'
                    ? 'No upcoming events'
                    : activeFilter === 'past'
                    ? 'No past events'
                    : 'No events yet'
                }
                message="Create your first event to get started"
                actionLabel="Create Event"
                onAction={() => navigate(`/clubs/${slug}/events/create`)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Floating Create Button */}
      <button
        onClick={() => navigate(`/clubs/${slug}/events/create`)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-accent hover:bg-accent/90 text-black rounded-full shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
        aria-label="Create event"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Confirmation Modal (non-recurring standalone events) #1207 */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Cancel Event"
        message={`Are you sure you want to cancel "${deleteTarget?.title}"? This action cannot be undone.`}
        confirmLabel="Cancel Event"
        variant="destructive"
      />

      {/* Delete Scope Modal (recurring standalone events) #1207 */}
      <DeleteScopeModal
        isOpen={showDeleteScope}
        onCancel={() => {
          setShowDeleteScope(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteScopeSelect}
        isDeleting={deleteEvent.isPending}
      />

      {/* Series Delete Modal (#1208) */}
      <SeriesDeleteModal
        isOpen={showSeriesDelete}
        onCancel={() => {
          setShowSeriesDelete(false);
          setSeriesDeleteTarget(null);
        }}
        onConfirm={handleSeriesDeleteConfirm}
        isDeleting={deleteSeries.isPending}
        seriesTitle={seriesDeleteTarget?.title || ''}
        upcomingCount={seriesDeleteTarget?.upcoming_count || 0}
      />
    </div>
  );
}
